/**
 * DispatcherActivityModal
 * Owner-only view showing all dispatcher actions, login history, and activity summary
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { getActivityLog, getLoginHistory, getActivitySummary } from '../services/activityLogService';

const ACTION_LABELS = {
  login: '🔑 Login',
  logout: '🚪 Logout',
  load_created: '📦 Load Created',
  load_updated: '✏️ Load Updated',
  load_deleted: '🗑️ Load Deleted',
  load_assigned: '👤 Load Assigned',
  status_changed: '🔄 Status Changed',
  customer_created: '🏢 Customer Created',
  customer_updated: '🏢 Customer Updated',
  truck_created: '🚛 Truck Created',
  truck_updated: '🚛 Truck Updated',
  driver_created: '👤 Driver Created',
  driver_updated: '👤 Driver Updated',
  settings_changed: '⚙️ Settings Changed',
};

const ACTION_COLORS = {
  login: '#4caf50',
  logout: '#9e9e9e',
  load_created: '#2196f3',
  load_updated: '#ff9800',
  load_deleted: '#f44336',
  load_assigned: '#9c27b0',
  status_changed: '#00bcd4',
  customer_created: '#4caf50',
  customer_updated: '#ff9800',
  truck_created: '#4caf50',
  truck_updated: '#ff9800',
  driver_created: '#4caf50',
  driver_updated: '#ff9800',
  settings_changed: '#607d8b',
};

function DispatcherActivityModal({ onClose }) {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [activities, setActivities] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterDays, setFilterDays] = useState(7);

  useEffect(() => {
    loadData();
  }, [filterAction, filterDays]);

  const loadData = async () => {
    setLoading(true);
    const endDate = new Date().toISOString();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - filterDays);

    const [activityData, loginData, summaryData] = await Promise.all([
      getActivityLog({
        limit: 200,
        actionType: filterAction === 'all' ? null : filterAction,
        startDate: startDate.toISOString(),
        endDate,
      }),
      getLoginHistory(null, filterDays),
      getActivitySummary(startDate.toISOString(), endDate),
    ]);

    setActivities(activityData);
    setLoginHistory(loginData);
    setSummary(summaryData);
    setLoading(false);
  };

  // Only owner can see this
  if (!isOwner) {
    return (
      <div style={modalStyle} onClick={onClose}>
        <div style={contentStyle} onClick={e => e.stopPropagation()}>
          <p style={{ color: '#f44336', textAlign: 'center', padding: '2rem' }}>Owner access required</p>
        </div>
      </div>
    );
  }

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatRelative = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // Calculate login sessions from login/logout pairs
  const getLoginSessions = () => {
    const sessions = [];
    const sorted = [...loginHistory].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    let currentSession = null;
    sorted.forEach(entry => {
      if (entry.action_type === 'login') {
        if (currentSession) {
          // Previous session never logged out
          currentSession.logoutAt = null;
          currentSession.duration = 'No logout recorded';
          sessions.push(currentSession);
        }
        currentSession = {
          userName: entry.user?.name || 'Unknown',
          loginAt: entry.created_at,
          logoutAt: null,
          duration: null,
        };
      } else if (entry.action_type === 'logout' && currentSession) {
        currentSession.logoutAt = entry.created_at;
        const durMin = Math.floor((new Date(entry.created_at) - new Date(currentSession.loginAt)) / 60000);
        const hrs = Math.floor(durMin / 60);
        const mins = durMin % 60;
        currentSession.duration = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
        sessions.push(currentSession);
        currentSession = null;
      }
    });
    
    // Handle open session
    if (currentSession) {
      currentSession.duration = 'Currently active';
      sessions.push(currentSession);
    }

    return sessions.reverse();
  };

  const totalActions = Object.values(summary).reduce((a, b) => a + b, 0);

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>🔍 Dispatcher Activity Monitor</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Owner-only — all dispatcher actions are logged
            </p>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid var(--surface-2)', paddingBottom: '0.5rem' }}>
          {['feed', 'logins', 'summary'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '0.5rem 1.25rem',
              backgroundColor: activeTab === tab ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              textTransform: 'capitalize',
            }}>
              {tab === 'feed' ? '📋 Activity Feed' : tab === 'logins' ? '🔑 Login History' : '📊 Summary'}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>TIME RANGE</label>
            <select value={filterDays} onChange={e => setFilterDays(Number(e.target.value))} style={selectStyle}>
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
          {activeTab === 'feed' && (
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>ACTION TYPE</label>
              <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={selectStyle}>
                <option value="all">All Actions</option>
                {Object.keys(ACTION_LABELS).map(key => (
                  <option key={key} value={key}>{ACTION_LABELS[key]}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {loading ? 'Loading...' : `${totalActions} actions in period`}
          </div>
        </div>

        {/* Content */}
        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-primary)' }}>Loading activity data...</div>
          ) : activeTab === 'feed' ? (
            /* ACTIVITY FEED */
            activities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No activity found for this period</div>
            ) : (
              <div>
                {activities.map(entry => (
                  <div key={entry.id} style={{
                    display: 'flex',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderBottom: '1px solid var(--surface-2)',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: ACTION_COLORS[entry.action_type] || '#666',
                      marginTop: '6px',
                      flexShrink: 0,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                          {ACTION_LABELS[entry.action_type] || entry.action_type}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                          {formatRelative(entry.created_at)}
                        </span>
                      </div>
                      {entry.notes && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {entry.notes}
                        </div>
                      )}
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {entry.user?.name || 'Unknown'} • {formatTime(entry.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'logins' ? (
            /* LOGIN HISTORY */
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['User', 'Login Time', 'Logout Time', 'Duration'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getLoginSessions().length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No login sessions found</td></tr>
                  ) : getLoginSessions().map((session, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{session.userName}</td>
                      <td style={tdStyle}>{formatTime(session.loginAt)}</td>
                      <td style={tdStyle}>{session.logoutAt ? formatTime(session.logoutAt) : '—'}</td>
                      <td style={{
                        ...tdStyle,
                        color: session.duration === 'Currently active' ? 'var(--color-success)' :
                               session.duration === 'No logout recorded' ? 'var(--color-warning)' : 'var(--text-primary)',
                        fontWeight: session.duration === 'Currently active' ? 'bold' : 'normal',
                      }}>{session.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* SUMMARY */
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {Object.entries(summary).sort((a, b) => b[1] - a[1]).map(([action, count]) => (
                  <div key={action} style={{
                    backgroundColor: 'var(--surface-2)',
                    borderRadius: '6px',
                    padding: '1rem',
                    borderLeft: `4px solid ${ACTION_COLORS[action] || '#666'}`,
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>{count}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      {ACTION_LABELS[action] || action}
                    </div>
                  </div>
                ))}
              </div>
              {totalActions === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No activity in this period</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const modalStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const contentStyle = {
  backgroundColor: 'var(--surface-1)',
  borderRadius: '10px',
  width: '95%',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflow: 'auto',
  padding: '2rem',
};

const closeBtn = {
  background: 'none',
  border: 'none',
  color: 'var(--text-primary)',
  fontSize: '1.5rem',
  cursor: 'pointer',
};

const selectStyle = {
  padding: '0.5rem 0.75rem',
  backgroundColor: 'var(--surface-2)',
  border: '1px solid var(--border-default)',
  borderRadius: '4px',
  color: 'var(--text-primary)',
  fontSize: '0.85rem',
  cursor: 'pointer',
};

const thStyle = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  borderBottom: '2px solid var(--surface-3)',
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = {
  padding: '0.6rem 0.75rem',
  borderBottom: '1px solid var(--surface-2)',
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
};

export default DispatcherActivityModal;
